import { FunctionComponent, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    MenuItem,
    Stack,
    SvgIcon,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DownloadIcon from '@heroicons/react/24/solid/ArrowDownTrayIcon';
import { usePagination } from '@starjack/shared-components';
import { StajackTableColumn, StarjackTable } from '@/components/Table/v2/Table';
import {
    getInsuranceExcel,
    InsuranceRow,
    ProductEnvironment,
    useGetInsuranceReportQuery,
} from '@/api/v2/insuranceReports';
import {
    downloadBlob,
    formatCount,
    formatEuro,
    formatPct,
} from '@/pageContents/reportsContent/format';

const CHANNEL_LABELS: Record<ProductEnvironment, string> = {
    PORTAL: 'Portal',
    INTEGRATION: 'Webshop',
    B2B: 'B2B',
    HOSPITALITY: 'Hospitality',
};

const ALL_CHANNELS: ProductEnvironment[] = [
    'PORTAL',
    'INTEGRATION',
    'B2B',
    'HOSPITALITY',
];

/**
 * ISO date of the LOCAL calendar day.
 *
 * Not toISOString(): that serialises UTC, and east of Greenwich local midnight is the
 * previous day there — in Vienna the whole settlement period would silently shift back by
 * one, invoicing 31 July twice and 31 August never.
 */
const isoDate = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
        date.getDate()
    ).padStart(2, '0')}`;

/**
 * The backend bound is exclusive, the picker means the day itself. Null for an incomplete
 * input — a half-typed or cleared date must not reach Date arithmetic, because
 * `new Date(NaN).toISOString()` throws out of the render function and there is no error
 * boundary above this page.
 */
const exclusiveEnd = (isoDay: string): string | null => {
    const [year, month, day] = isoDay.split('-').map(Number);
    if (!year || !month || !day) return null;
    // Local components, so the Date constructor rolls month and year itself and the day
    // count stays right across both daylight-saving switches.
    return isoDate(new Date(year, month - 1, day + 1));
};

/** First and last day of the month before the current one — the period usually settled. */
const defaultPeriod = () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: isoDate(from), to: isoDate(to) };
};

export const InsuranceContent: FunctionComponent = () => {
    const initial = useMemo(defaultPeriod, []);
    const [from, setFrom] = useState(initial.from);
    const [to, setTo] = useState(initial.to);
    const [channel, setChannel] = useState<ProductEnvironment | 'ALL'>('ALL');
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(false);

    const exclusiveTo = exclusiveEnd(to);
    const rangeIsValid = Boolean(from && exclusiveTo) && from <= to;

    const query = useMemo(
        () => ({
            from,
            to: exclusiveTo ?? from,
            environments: channel === 'ALL' ? undefined : [channel],
        }),
        [from, exclusiveTo, channel]
    );

    // currentData, not data: data keeps the last successful result across argument
    // changes, which would show last month's figures under this month's heading.
    const { currentData, isFetching, isError } = useGetInsuranceReportQuery(
        query,
        {
            skip: !rangeIsValid,
            // The Excel export always fetches fresh. Serving the table from cache would let the
            // two disagree on the invoice figure for the same selection — and the number on
            // screen is the one somebody reads off.
            refetchOnMountOrArgChange: true,
        }
    );

    const download = async () => {
        setIsDownloading(true);
        setDownloadError(false);
        const blob = await getInsuranceExcel(query);
        if (blob) {
            downloadBlob(blob, `versicherungen-${from}-bis-${to}.xlsx`);
        } else {
            setDownloadError(true);
        }
        setIsDownloading(false);
    };

    return (
        <Stack spacing={3}>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ md: 'center' }}
            >
                <TextField
                    label='Von'
                    type='date'
                    size='small'
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label='Bis'
                    type='date'
                    size='small'
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label='Vertriebskanal'
                    select
                    size='small'
                    value={channel}
                    onChange={(e) =>
                        setChannel(e.target.value as ProductEnvironment | 'ALL')
                    }
                    sx={{ minWidth: 180 }}
                >
                    <MenuItem value='ALL'>Alle Kanäle</MenuItem>
                    {ALL_CHANNELS.map((c) => (
                        <MenuItem key={c} value={c}>
                            {CHANNEL_LABELS[c]}
                        </MenuItem>
                    ))}
                </TextField>

                <Button
                    variant='outlined'
                    disabled={isDownloading || !rangeIsValid}
                    onClick={download}
                    startIcon={
                        <SvgIcon fontSize='small'>
                            <DownloadIcon />
                        </SvgIcon>
                    }
                >
                    {isDownloading ? 'Wird erstellt…' : 'Excel'}
                </Button>

                {isFetching && <CircularProgress size={20} />}
            </Stack>

            {!rangeIsValid && (
                <Alert severity='warning'>
                    Bitte einen vollständigen Zeitraum wählen, bei dem „Von"
                    nicht nach „Bis" liegt.
                </Alert>
            )}

            {isError && (
                <Alert severity='error'>
                    Die Auswertung konnte nicht geladen werden.
                </Alert>
            )}

            {downloadError && (
                <Alert severity='error' onClose={() => setDownloadError(false)}>
                    Der Excel-Export ist fehlgeschlagen.
                </Alert>
            )}

            {currentData && (
                <>
                    <Totals report={currentData} />
                    <SalesTable rows={currentData.rows} />
                    <TerminationsTable
                        rows={currentData.terminationsInPeriod}
                    />
                    <WithoutSalesTable
                        rows={currentData.operatorsWithoutInsurance}
                    />
                </>
            )}
        </Stack>
    );
};

const Totals: FunctionComponent<{
    report: NonNullable<
        ReturnType<typeof useGetInsuranceReportQuery>['currentData']
    >;
}> = ({ report }) => (
    <Card>
        <CardContent>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={4}
                flexWrap='wrap'
            >
                <Figure
                    label='Prämie policiert'
                    hint='Vom Versicherer bestätigt — die Rechnungsbasis. Ändert sich nicht mehr, wenn später storniert wird.'
                    value={formatEuro(report.totals.confirmedPremium)}
                />
                <Figure
                    label='Prämie brutto'
                    hint='Alles, was im Zeitraum verkauft wurde, inkl. unbestätigter Policen.'
                    value={formatEuro(report.totals.grossPremium)}
                />
                <Figure
                    label='Unbestätigt'
                    hint='Bezahlt, aber vom Versicherer nie bestätigt. Klärfall — nicht fakturieren.'
                    value={formatEuro(report.totals.unconfirmedPremium)}
                />
                <Figure
                    label='Offen'
                    hint='Verkauft und noch nicht policiert. Teil der Statusaufteilung.'
                    value={formatEuro(report.totals.openPremium)}
                />
                <Figure
                    label='Heute noch aktiv'
                    hint='Policiert und seither weder storniert, widerrufen noch gesperrt. Kontrollzahl.'
                    value={formatEuro(report.totals.activePremium)}
                />
                <Figure
                    label='Policen'
                    value={formatCount(report.totals.soldCount)}
                />
            </Stack>
        </CardContent>
    </Card>
);

const Figure: FunctionComponent<{
    label: string;
    value: string;
    hint?: string;
}> = ({ label, value, hint }) => (
    <Stack spacing={0.5}>
        <Tooltip title={hint ?? ''} placement='top'>
            <Typography variant='body2' color='text.secondary'>
                {label}
            </Typography>
        </Tooltip>
        <Typography variant='h6'>{value}</Typography>
    </Stack>
);

// Numeric columns stay right-aligned like before
const RIGHT = {
    headerCellProps: { align: 'right' },
    rowCellProps: { align: 'right' },
} as const;

const liftOperatorColumn = {
    label: 'Bergbahn',
    itemTransform: (r: { liftOperatorName?: string; liftOperatorId: string }) =>
        r.liftOperatorName ?? r.liftOperatorId,
};

const tariffColumn = {
    label: 'Tarif',
    itemTransform: (r: { tariffName?: string; tariffIdentifier: string }) =>
        r.tariffName ?? r.tariffIdentifier,
};

const PaginatedTable = <T,>({
    rows,
    columns,
}: {
    rows: T[];
    columns: StajackTableColumn<T>[];
}) => {
    const { currentItems, page, setNextPage, setItemsPerPage, itemsPerPage } =
        usePagination({ items: rows, itemsPerPage: 10 });

    return (
        <StarjackTable<T>
            count={rows.length}
            items={currentItems}
            columns={columns}
            currentPage={page}
            onPageChange={setNextPage}
            rowsPerPage={itemsPerPage}
            onRowsPerPageChange={setItemsPerPage}
        />
    );
};

const SalesTable: FunctionComponent<{ rows: InsuranceRow[] }> = ({ rows }) => {
    if (!rows.length) {
        return (
            <Alert severity='info'>
                Im gewählten Zeitraum wurde keine Versicherung verkauft.
            </Alert>
        );
    }

    return (
        <Stack spacing={1}>
            <Typography variant='h6'>Verkäufe je Bergbahn und Tarif</Typography>
            <PaginatedTable<InsuranceRow>
                rows={rows}
                columns={[
                    liftOperatorColumn,
                    tariffColumn,
                    {
                        label: 'Verkauft',
                        itemTransform: (r) => formatCount(r.soldCount),
                        options: RIGHT,
                    },
                    {
                        label: 'Prämie policiert',
                        itemTransform: (r) => formatEuro(r.confirmedPremium),
                        options: RIGHT,
                    },
                    {
                        label: 'Unbestätigt',
                        itemTransform: (r) =>
                            r.unconfirmedCount > 0
                                ? `${formatCount(r.unconfirmedCount)} · ${formatEuro(r.unconfirmedPremium)}`
                                : '—',
                        options: RIGHT,
                    },
                    {
                        label: 'Aktiv',
                        itemTransform: (r) => formatCount(r.activeCount),
                        options: RIGHT,
                    },
                    {
                        label: 'Offen',
                        itemTransform: (r) => formatCount(r.openCount),
                        options: RIGHT,
                    },
                    {
                        label: 'Storno',
                        itemTransform: (r) => formatCount(r.canceledCount),
                        options: RIGHT,
                    },
                    {
                        label: 'Widerruf',
                        itemTransform: (r) => formatCount(r.revokedCount),
                        options: RIGHT,
                    },
                    {
                        label: 'Gesperrt',
                        itemTransform: (r) =>
                            formatCount(r.permissionBlockedCount),
                        options: RIGHT,
                    },
                    {
                        label: 'Anteil an Tickets',
                        itemTransform: (r) => formatPct(r.attachRatePct),
                        options: RIGHT,
                    },
                    {
                        label: 'Kanäle',
                        itemTransform: (r) => (
                            <Stack
                                direction='row'
                                spacing={0.5}
                                flexWrap='wrap'
                                useFlexGap
                            >
                                {r.channels.map((c) => (
                                    <Chip
                                        key={`${c.environment}-${c.externalPayment}`}
                                        size='small'
                                        variant='outlined'
                                        label={`${CHANNEL_LABELS[c.environment]}${
                                            c.externalPayment ? ' (Bahn)' : ''
                                        }: ${c.soldCount}`}
                                    />
                                ))}
                            </Stack>
                        ),
                    },
                ]}
            />
            <Typography variant='caption' color='text.secondary'>
                „Anteil an Tickets" bezieht sich auf alle verkauften Tickets der
                Bahn, nicht nur auf die versicherbaren — die Werte mehrerer
                Tarife teilen sich denselben Nenner und dürfen nicht addiert
                werden. Aktiv, Offen, Storno, Widerruf und Gesperrt ergeben
                zusammen die Bruttoprämie; „Unbestätigt" ist eine überlappende
                Zweitsicht und gehört nicht in diese Summe.
            </Typography>
        </Stack>
    );
};

const TerminationsTable: FunctionComponent<{
    rows: InsuranceReportTerminations;
}> = ({ rows }) => {
    if (!rows.length) return null;

    return (
        <Stack spacing={1}>
            <Typography variant='h6'>
                Stornos und Widerrufe im Zeitraum
            </Typography>
            <Typography variant='body2' color='text.secondary'>
                Was in diesem Zeitraum zurückgenommen wurde — unabhängig davon,
                wann es verkauft wurde. Diese Tabelle ist die Gutschriftseite
                und stimmt bewusst nicht mit den Storno-Spalten oben überein.
            </Typography>
            <PaginatedTable<InsuranceReportTerminations[number]>
                rows={rows}
                columns={[
                    liftOperatorColumn,
                    tariffColumn,
                    {
                        label: 'Stornos',
                        itemTransform: (r) => formatCount(r.canceledCount),
                        options: RIGHT,
                    },
                    {
                        label: 'Prämie storniert',
                        itemTransform: (r) => formatEuro(r.canceledPremium),
                        options: RIGHT,
                    },
                    {
                        label: 'Widerrufe',
                        itemTransform: (r) => formatCount(r.revokedCount),
                        options: RIGHT,
                    },
                    {
                        label: 'Prämie widerrufen',
                        itemTransform: (r) => formatEuro(r.revokedPremium),
                        options: RIGHT,
                    },
                    {
                        label: 'Davon Kundengutschrift',
                        itemTransform: (r) => formatEuro(r.refundedAmount),
                        options: RIGHT,
                    },
                ]}
            />
            <Typography variant='caption' color='text.secondary'>
                Nicht jede Stornierung erstattet Geld — ein Embargo setzt das
                Storno ohne Kundengutschrift. Deshalb steht die tatsächliche
                Gutschrift als eigene Spalte daneben.
            </Typography>
        </Stack>
    );
};

const WithoutSalesTable: FunctionComponent<{
    rows: InsuranceReportWithoutSales;
}> = ({ rows }) => {
    if (!rows.length) return null;

    return (
        <Stack spacing={1}>
            <Typography variant='h6'>
                Bergbahnen ohne Versicherungsverkauf
            </Typography>
            <PaginatedTable<InsuranceReportWithoutSales[number]>
                rows={rows}
                columns={[
                    liftOperatorColumn,
                    {
                        label: 'Tickets im Zeitraum',
                        itemTransform: (r) => formatCount(r.ticketsSold),
                        options: RIGHT,
                    },
                ]}
            />
        </Stack>
    );
};

type InsuranceReportTerminations = NonNullable<
    ReturnType<typeof useGetInsuranceReportQuery>['currentData']
>['terminationsInPeriod'];

type InsuranceReportWithoutSales = NonNullable<
    ReturnType<typeof useGetInsuranceReportQuery>['currentData']
>['operatorsWithoutInsurance'];
